import { useMutation, useQuery } from "@apollo/client"
import { yupResolver } from "@hookform/resolvers/yup"
import { camelCase, pick } from "lodash-es"
import React, { useCallback, useState } from "react"
import { useForm, type SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import CustomerForm, { type CustomerFormInputs, customerSchema } from "./customer_form"
import Shell from "./shell"
import { graphql } from "@/api"
import { ClientRole } from "@/api/graphql"
import ClientData from "@/common/client_data"
import DotSpinner from "@/common/components/dot_spinner"
import FormForm from "@/common/components/form_form"
import { showStreet2, streetNrRequired, stateRequired } from "@/common/locales"
import { Routemap } from "@/common/routes"
import Util from "@/common/util"

const profileDoc = graphql(`
  query CustomerProfile {
    currentCustomer {
      id
      firstName
      lastName
      company
      vatId
      orgId
      glnNumber
      reference
      houseNumber
      street
      street2
      state
      zipcode
      city
      country
      deliveryFirstName
      deliveryLastName
      deliveryCompany
      deliveryHouseNumber
      deliveryStreet
      deliveryStreet2
      deliveryState
      deliveryZipcode
      deliveryCity
      deliveryCountry
      deliveryPhone
      phone1
      invoiceEmail
      differentDeliveryAddress
      business
      newsletterEnabled
      marketingConsents
      __typename
    }
  }
`)

const updateDoc = graphql(`
  mutation CustomerUpdate($customer: CustomerInput!) {
    customerUpdate(customer: $customer) {
      success
      notice

      customer {
        id
        firstName
        lastName
        company
        vatId
        orgId
        glnNumber
        reference
        houseNumber
        street
        street2
        state
        zipcode
        city
        country
        phone1
        invoiceEmail
        differentDeliveryAddress
        deliveryFirstName
        deliveryLastName
        deliveryCompany
        deliveryHouseNumber
        deliveryStreet2
        deliveryStreet
        deliveryZipcode
        deliveryCity
        deliveryState
        deliveryCountry
        deliveryPhone
        business
        newsletterEnabled
        marketingConsents
        __typename
      }

      errors {
        message
        path
      }
    }
  }
`)

interface ProfileProps {
  customer: string
}

const schema = customerSchema(false)

const Profile: React.FC<ProfileProps> = ({ customer }) => {
  const { t } = useTranslation()
  const [mutation] = useMutation(updateDoc, { errorPolicy: "all" })
  const formMethods = useForm<CustomerFormInputs>({
    resolver: yupResolver(schema),
  })
  const {
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    formState,
  } = formMethods
  const [message, setMessage] = useState<string>()

  const result = useQuery(profileDoc, {
    fetchPolicy: "network-only",
    onCompleted: data => {
      const customer = data.currentCustomer

      if (!customer) {
        return
      }

      reset({
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        company: customer.company,
        vatId: customer.vatId,
        orgId: customer.orgId,
        glnNumber: customer.glnNumber,
        reference: customer.reference,
        street: customer.street,
        street2: customer.street2,
        houseNumber: customer.houseNumber,
        zipcode: customer.zipcode,
        city: customer.city,
        state: customer.state,
        country: customer.country,
        deliveryFirstName: customer.deliveryFirstName,
        deliveryLastName: customer.deliveryLastName,
        deliveryCompany: customer.deliveryCompany,
        deliveryStreet: customer.deliveryStreet,
        deliveryStreet2: customer.deliveryStreet2,
        deliveryHouseNumber: customer.deliveryHouseNumber,
        deliveryZipcode: customer.deliveryZipcode,
        deliveryCity: customer.deliveryCity,
        deliveryState: customer.deliveryState,
        deliveryCountry: customer.deliveryCountry,
        phone1: customer.phone1,
        deliveryPhone: customer.deliveryPhone,
        invoiceEmail: customer.invoiceEmail,
        differentDeliveryAddress: !!customer.differentDeliveryAddress,
        newsletterEnabled: !!customer.newsletterEnabled,
        clientRole: customer.business ? ClientRole.business : ClientRole.consumer,
      })
    },
  })

  const submit: SubmitHandler<CustomerFormInputs> = useCallback(
    async (values, e) => {
      e?.preventDefault()

      setMessage(undefined)

      const form = pick(
        values,
        "firstName",
        "lastName",
        "company",
        "vatId",
        "orgId",
        "glnNumber",
        "reference",
        "street",
        "street2",
        "houseNumber",
        "zipcode",
        "city",
        "state",
        "country",
        "deliveryFirstName",
        "deliveryLastName",
        "deliveryCompany",
        "deliveryStreet",
        "deliveryStreet2",
        "deliveryHouseNumber",
        "deliveryZipcode",
        "deliveryCity",
        "deliveryState",
        "deliveryCountry",
        "phone1",
        "deliveryPhone",
        "invoiceEmail",
        "differentDeliveryAddress",
        "newsletterEnabled"
      )

      if (
        values.clientRole == ClientRole.consumer ||
        !ClientData.vatEnabledCountries?.includes(form.deliveryCountry || "")
      ) {
        form.vatId = ""
      }

      if (values.clientRole == ClientRole.consumer) {
        form.company = ""
        form.reference = ""
      }

      if (form.country && !stateRequired(form.country)) {
        form.state = ""
      }

      if (form.country && !streetNrRequired(form.country)) {
        form.houseNumber = ""
      }

      if (form.country && !showStreet2(form.country)) {
        form.street2 = ""
      }

      if (form.deliveryCountry && !stateRequired(form.deliveryCountry)) {
        form.deliveryState = ""
      }

      if (form.deliveryCountry && !streetNrRequired(form.deliveryCountry)) {
        form.deliveryHouseNumber = ""
      }

      if (form.deliveryCountry && !showStreet2(form.deliveryCountry)) {
        form.deliveryStreet2 = ""
      }

      const result = await mutation({
        variables: { customer: { ...form, email: customer } },
      })

      for (const error of result.errors || []) {
        if (error.message === "login_required") {
          location.reload()
        }
      }

      const data = result.data?.customerUpdate

      if (data) {
        const errors = data.errors

        if (errors && errors.length > 0) {
          for (const error of errors) {
            if (error.path && error.path.length > 0) {
              setError(camelCase(error.path[0]), { type: "validate", message: error.message })
            }
          }

          return
        }

        if (data.notice) {
          setMessage(data.notice)
        }
      }
    },
    [customer, mutation, setError]
  )

  const disabled = result.loading || formState.isSubmitting

  return (
    <Shell customer={customer} profile>
      <div className="customerarea profile">
        <FormForm id="update_form" onSubmit={handleSubmit(submit)} formMethods={formMethods}>
          <div className="grid gap-2 md:grid-cols-3">
            <div className="col-lg-3 col-md-3 col-xs-12">
              <p className="paragraph-sm">{t("customer.your_info")}</p>
              <p className="paragraph-2xs">{t("customer.id")}:</p>
              <p className="paragraph-sm data-hj-suppress">{customer}</p>
              <Link
                to={Routemap.customerZoneChangePassword(t)}
                className="button button-primary button-medium change-password"
              >
                {t("customer.change_password")}
              </Link>
            </div>
            {result.loading ? (
              <div className="col-span-2">
                <DotSpinner center />
              </div>
            ) : (
              <CustomerForm customer={customer} customerData={result.data?.currentCustomer} />
            )}
          </div>
          <div className="grid md:grid-cols-3">
            <div></div>
            <div className="center col-span-2">
              {errors.vatId && (
                <div className="vatalert">
                  <div className="alert alert-error">
                    {t(`${errors.vatId.message}`, {
                      vat_code: Util.validations.vat_number_example[result.data?.currentCustomer?.country || ""],
                    })}
                  </div>
                </div>
              )}
              {errors.orgId && (
                <div className="orgalert">
                  <div className="alert alert-error">{t(`${errors.orgId.message}`)}</div>
                </div>
              )}
              {errors.glnNumber && (
                <div className="glnalert">
                  <div className="alert alert-error">{t(`${errors.glnNumber.message}`)}</div>
                </div>
              )}
              <button id="commit" className="button button-primary button-medium" type="submit" disabled={disabled}>
                {t("misc.apply")}
              </button>
              {message && (
                <p className="form_message center bg-success" style={{ margin: "0 auto" }}>
                  {message}
                </p>
              )}
            </div>
          </div>
        </FormForm>
      </div>
    </Shell>
  )
}

export default Profile
